package br.com.vvrefeicoes.portal.feature.operacional

import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Modifier
import androidx.compose.ui.geometry.Offset
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.input.pointer.PointerEventType
import androidx.compose.ui.input.pointer.pointerInput
import br.com.vvrefeicoes.portal.core.util.MoneyFormatter
import io.github.koalaplot.core.pie.DefaultSlice
import io.github.koalaplot.core.pie.PieChart
import io.github.koalaplot.core.util.ExperimentalKoalaPlotApi
import org.jetbrains.compose.resources.stringResource
import portalvv.shared.generated.resources.Res
import portalvv.shared.generated.resources.operational_detailed_total
import kotlin.math.PI
import kotlin.math.abs
import kotlin.math.atan2
import kotlin.math.hypot
import kotlin.math.round

data class DonutSlice(
    val label: String,
    val value: Long,
    val color: Color,
)

@OptIn(ExperimentalKoalaPlotApi::class)
@Composable
fun DonutChart(
    slices: List<DonutSlice>,
    modifier: Modifier = Modifier,
    formatValue: (Long) -> String = { money(it) },
) {
    if (slices.isEmpty()) return
    val total = slices.sumOf { it.value }.coerceAtLeast(1L)
    val fractions = slices.map { it.value.toDouble() / total }
    var hovered by remember { mutableStateOf(-1) }
    val tooltipText =
        if (hovered in slices.indices) {
            val slice = slices[hovered]
            "${slice.label} — ${formatValue(slice.value)} (${percentText(slice.value.toDouble() / total * 100)})"
        } else {
            "${stringResource(Res.string.operational_detailed_total)}: ${formatValue(total)}"
        }
    Box(
        modifier =
            modifier.pointerInput(slices) {
                awaitPointerEventScope {
                    while (true) {
                        val event = awaitPointerEvent()
                        hovered =
                            if (event.type == PointerEventType.Exit) {
                                -1
                            } else {
                                event.changes.firstOrNull()?.position?.let { position ->
                                    sliceIndexAt(position, size.width, size.height, fractions)
                                } ?: -1
                            }
                    }
                }
            },
    ) {
        HoverTooltip(text = tooltipText) {
            PieChart(
                values = slices.map { it.value.toFloat() },
                modifier = Modifier.fillMaxSize(),
                slice = { index -> DefaultSlice(color = slices[index].color) },
                labelConnector = {},
                holeSize = 0.6f,
            )
        }
    }
}

private fun sliceIndexAt(
    position: Offset,
    width: Int,
    height: Int,
    fractions: List<Double>,
): Int {
    if (fractions.isEmpty()) return -1
    val radius = minOf(width, height) / 2f
    val dx = position.x - width / 2f
    val dy = position.y - height / 2f
    val distance = hypot(dx, dy)
    if (distance < radius * 0.6f || distance > radius * 0.95f) return -1
    val angleDegrees = (atan2(dy, dx) * 180.0 / PI).toFloat()
    val fromTop = ((angleDegrees + 90f) % 360f + 360f) % 360f
    val target = fromTop / 360.0
    var cumulative = 0.0
    fractions.forEachIndexed { index, fraction ->
        cumulative += fraction
        if (target < cumulative) return index
    }
    return fractions.lastIndex
}

private fun money(cents: Long): String = "R$ ${MoneyFormatter.formatCents(cents)}"

private fun percentText(value: Double): String {
    val scaled = round(value * 10).toLong()
    return "${scaled / 10},${abs(scaled % 10)}%"
}
